import { ListBox } from 'primereact/listbox';
import { RequestStatus } from '@/shared/model/requestStatus';
import { RequestError, RequestLoading } from '@/shared/ui/RequestState';
import type { AddressOption } from './addressOption';
import classes from './AddressListField.module.scss';

interface AddressListFieldProps {
    name: string;
    label: string;
    ownerId: string;
    emptyOwnerMessage: string;
    emptyListMessage: string;
    helpText: string;
    value: string;
    displayText?: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    error?: string;
    disabled: boolean;
    isRequired: boolean;
    options: AddressOption[];
    status: RequestStatus;
    requestError?: string | null;
    onRetry: () => void;
}

/** Отображает адрес и отметку его выбора в списке. */
const AddressOptionRow = ({ option, selected }: { option: AddressOption; selected: boolean }) => (
    <div className={classes.addressRow}>
        <span
            className={classes.addressIcon}
            aria-hidden="true"
        >
            <i className="pi pi-map-marker" />
        </span>
        <span className={classes.addressInfo}>
            <span className={classes.addressTitle}>{option.title}</span>
            {option.location &&
                <span className={classes.addressLocation}>{option.location}</span>
            }
        </span>
        {option.isFavorite &&
            <span className={classes.favoriteBadge}><i className="pi pi-star-fill" /> Избранный</span>
        }
        <span
            className={classes.selectionMark}
            aria-hidden="true"
        >
            {selected &&
                <i className="pi pi-check" />
            }
        </span>
    </div>
);

/** Показывает адреса открытым списком с выбором одного адреса. */
export const AddressListField = (
    {
        name,
        label,
        ownerId,
        emptyOwnerMessage,
        emptyListMessage,
        helpText,
        value,
        displayText,
        onChange,
        onBlur,
        error,
        disabled,
        isRequired,
        options,
        status,
        requestError,
        onRetry,
    }: AddressListFieldProps
) => {
    const selectedFallback = value && displayText?.trim() && !options.some((option) => option.addressId === value)
        ? {
            addressId: value,
            title: displayText.trim(),
            location: '',
            isFavorite: false
        }
        : null;

    const visibleOptions = selectedFallback
        ? [selectedFallback, ...options]
        : options;

    const labelId = `${name}-label`;
    const helpId = `${name}-help`;
    const errorId = `${name}-error`;

    return (
        <div className={classes.field}>
            <div className={classes.header}>
                <label
                    id={labelId}
                    htmlFor={name}
                    className={classes.label}
                >
                    {label} {isRequired &&
                        <span
                            className={classes.required}
                            aria-hidden="true"
                        >
                            *
                        </span>
                    }
                </label>
                {status === RequestStatus.Success && options.length > 0 && (
                    <span className={classes.counter}>Адресов: {options.length}</span>
                )}
            </div>

            {!ownerId && (
                <div className={classes.emptyState}>
                    <i
                        className="pi pi-map-marker"
                        aria-hidden="true"
                    />
                    <span>{emptyOwnerMessage}</span>
                </div>
            )}

            {ownerId && status === RequestStatus.Loading && (
                <div className={classes.statePanel}><RequestLoading message="Загрузка адресов..." /></div>
            )}

            {ownerId && status === RequestStatus.Error && (
                <div className={classes.statePanel}>
                    <RequestError
                        message={requestError ?? 'Не удалось загрузить адреса'}
                        onRetry={onRetry}
                    />
                </div>
            )}

            {ownerId && status === RequestStatus.Success && visibleOptions.length === 0 && (
                <div className={classes.emptyState}>
                    <i
                        className="pi pi-map-marker"
                        aria-hidden="true"
                    />
                    <span>{emptyListMessage}</span>
                </div>
            )}

            {ownerId && visibleOptions.length > 0 && (
                <ListBox
                    id={name}
                    aria-labelledby={labelId}
                    aria-describedby={error
                        ? `${helpId} ${errorId}`
                        : helpId
                    }
                    aria-invalid={Boolean(error)}
                    tabIndex={-1}
                    pt={{
                        root: { 'aria-required': undefined },
                        list: {
                            tabIndex: disabled
                                ? -1
                                : 0,
                            'aria-required': isRequired
                        },
                    }}
                    aria-required={isRequired}
                    invalid={Boolean(error)}
                    className={classes.listBox}
                    options={visibleOptions}
                    optionLabel="title"
                    optionValue="addressId"
                    value={value || null}
                    onChange={(event) => onChange(typeof event.value === 'string'
                        ? event.value
                        : '')}
                    onBlur={onBlur}
                    itemTemplate={(option: AddressOption) => (
                        <AddressOptionRow
                            option={option}
                            selected={value === option.addressId}
                        />
                    )}
                    disabled={disabled || status !== RequestStatus.Success}
                    filter={visibleOptions.length > 5}
                    filterBy="title,location"
                    filterPlaceholder="Найти адрес"
                    listStyle={{ maxHeight: '25rem' }}
                />
            )}

            {ownerId && status === RequestStatus.Success && visibleOptions.length > 0 && (
                <p
                    id={helpId}
                    className={classes.helpText}
                >
                    {helpText}
                </p>
            )}
            {error &&
                <p
                    id={errorId}
                    className={classes.error}
                    role="alert"
                >
                    {error}
                </p>
            }
        </div>
    );
};
